import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import { Field, SaveBar, inputClass, saveCmsDoc } from "../cmsApi.jsx";
import IconPicker from "../components/IconPicker.jsx";
import CmsWithMediaSpecs from "../components/CmsMediaSpecsPanel.jsx";
import { ResolveCmsIcon } from "../../components/ResolveCmsIcon";
import {
  createNavItem,
  formatNavLink,
  normalizeNavigationDoc,
} from "../../lib/navigationMenu";

export default function CmsNavigationEditor() {
  const { data, loading, source } = useCmsDoc("site", "navigation", normalizeNavigationDoc({}));
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!data) return;
    setForm(normalizeNavigationDoc(structuredClone(data)));
  }, [data]);

  if (loading || !form) {
    return <p className="text-sm text-brand-dark/50">Cargando menú responsivo…</p>;
  }

  const setItems = (next) => setForm((prev) => ({ ...prev, items: next }));

  const updateItem = (idx, patch) => {
    const next = [...form.items];
    next[idx] = { ...next[idx], ...patch };
    setItems(next);
  };

  const addItem = () => setItems([...form.items, createNavItem()]);

  const removeItem = (idx) => setItems(form.items.filter((_, i) => i !== idx));

  const moveItem = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= form.items.length) return;
    const next = [...form.items];
    const [row] = next.splice(idx, 1);
    next.splice(target, 0, row);
    setItems(next);
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const { id, updatedAt, updatedBy, ...payload } = form;
      await saveCmsDoc("site", "navigation", normalizeNavigationDoc(payload));
      setMessage("Menú responsivo guardado.");
    } catch (err) {
      setError(err?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="font-display text-3xl font-black">Menú responsivo</h2>
        <p className="text-sm text-brand-dark/55 mt-1">
          Como el Footer: elija icono del catálogo (Lucide, Marca o archivo subido con etiqueta{" "}
          <strong>Menú responsivo</strong>). Fuente: {source}
        </p>
      </div>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-bold text-lg">Ítems de navegación</h3>
            <p className="text-sm text-brand-dark/55 mt-1">
              Elija icono por ítem (marca, Lucide, React Icons o subidos). Marque dónde se muestra.
            </p>
          </div>
          <button
            type="button"
            onClick={addItem}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-brand-primary"
          >
            <Plus size={14} /> Agregar
          </button>
        </div>

        <div className="flex flex-wrap justify-center gap-3 rounded-2xl bg-[#050e08] px-6 py-5">
          {form.items
            .filter((row) => row.showInBottomNav)
            .map((row) => (
              <span
                key={row.id}
                title={row.label}
                className="text-white inline-flex flex-col items-center gap-1"
              >
                <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                  <ResolveCmsIcon icon={row.icon} size={20} className="text-white" alt={row.label} />
                </span>
                <span className="text-[9px] font-bold text-white/70 max-w-[4.5rem] truncate">
                  {row.label}
                </span>
              </span>
            ))}
        </div>

        <ul className="space-y-4">
          {form.items.map((row, idx) => (
            <li
              key={row.id}
              className="rounded-2xl border border-brand-soft/80 bg-[#f8faf9] p-4 space-y-3"
            >
              <div className="flex items-center gap-2">
                <span className="w-8 text-center text-xs font-black text-brand-dark/40">{idx + 1}</span>
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    aria-label="Subir"
                    disabled={idx === 0}
                    onClick={() => moveItem(idx, -1)}
                    className="p-1 rounded-md text-brand-dark/50 hover:bg-white disabled:opacity-30"
                  >
                    <ChevronUp size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="Bajar"
                    disabled={idx === form.items.length - 1}
                    onClick={() => moveItem(idx, 1)}
                    className="p-1 rounded-md text-brand-dark/50 hover:bg-white disabled:opacity-30"
                  >
                    <ChevronDown size={16} />
                  </button>
                </div>
                <div className="flex-1 grid sm:grid-cols-2 gap-3">
                  <Field label="Etiqueta">
                    <input
                      className={inputClass}
                      value={row.label}
                      onChange={(e) => updateItem(idx, { label: e.target.value })}
                    />
                  </Field>
                  <Field label="Enlace">
                    <input
                      className={inputClass}
                      value={row.link}
                      onChange={(e) => updateItem(idx, { link: e.target.value })}
                      placeholder="/servicios"
                    />
                  </Field>
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="inline-flex items-center gap-1 text-xs font-bold text-red-600 px-2 py-2"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <div className="flex flex-wrap gap-4 text-xs font-bold">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={row.showInMobileMenu !== false}
                    onChange={(e) => updateItem(idx, { showInMobileMenu: e.target.checked })}
                  />
                  Menú hamburguesa
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={row.showInBottomNav !== false}
                    onChange={(e) => updateItem(idx, { showInBottomNav: e.target.checked })}
                  />
                  Barra inferior
                </label>
                <span className="text-brand-dark/40 font-medium">
                  Ruta: {formatNavLink(row.link)}
                </span>
              </div>

              <CmsWithMediaSpecs variant="icons" sticky={false}>
                <IconPicker
                  label="Icono (menú responsivo)"
                  value={row.icon}
                  onChange={(icon) => updateItem(idx, { icon })}
                  preferredClassification="navegacion"
                />
              </CmsWithMediaSpecs>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Formación (botón especial)</h3>
        <label className="inline-flex items-center gap-2 text-sm font-bold cursor-pointer">
          <input
            type="checkbox"
            checked={form.formation?.enabled !== false}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                formation: { ...prev.formation, enabled: e.target.checked },
              }))
            }
          />
          Mostrar en menú responsivo
        </label>
        <Field label="Etiqueta">
          <input
            className={inputClass}
            value={form.formation?.label || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                formation: { ...prev.formation, label: e.target.value },
              }))
            }
          />
        </Field>
        <Field label="URL (abre en pestaña nueva)">
          <input
            className={inputClass}
            type="url"
            placeholder="https://academia.agdysa.com/"
            value={form.formation?.url || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                formation: { ...prev.formation, url: e.target.value },
              }))
            }
          />
        </Field>
        <CmsWithMediaSpecs variant="icons" sticky={false}>
          <IconPicker
            label="Icono Formación"
            value={form.formation?.icon || "lucide:GraduationCap"}
            preferredClassification="navegacion"
            onChange={(icon) =>
              setForm((prev) => ({
                ...prev,
                formation: { ...prev.formation, icon },
              }))
            }
          />
        </CmsWithMediaSpecs>
      </section>

      <SaveBar saving={saving} onSave={onSave} message={message} error={error} />
    </div>
  );
}
